import { Icon } from '@/components/Icon';
import { useAuth } from '@/features/auth/AuthProvider';
import { signOutOfFirebase } from '@/features/auth/signInWithGoogle';
import { apiCall } from '@/lib/api';
import { type ThemeMode, setThemeMode, themeMode } from '@/stores/theme.store';
import { useNavigate } from '@solidjs/router';
import { createQuery } from '@tanstack/solid-query';
import { getApiVersion } from '@uchat-team-management/shared';
import { For, type JSX, Show, onCleanup, onMount } from 'solid-js';

const THEME_MODES: { value: ThemeMode; label: string }[] = [
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
  { value: 'system', label: 'System' },
];

export const AccountMenu = (props: { onClose: () => void }): JSX.Element => {
  const { session } = useAuth();
  const navigate = useNavigate();
  let containerRef: HTMLDivElement | undefined;

  // Menu mounts on open, so this fetches lazily; the build id never changes
  // within a session.
  const apiVersion = createQuery(() => ({
    queryKey: ['api-version'],
    queryFn: () => apiCall(getApiVersion, {}),
    staleTime: Number.POSITIVE_INFINITY,
  }));

  onMount(() => {
    const onDocClick = (e: MouseEvent) => {
      if (containerRef && !containerRef.contains(e.target as Node)) {
        props.onClose();
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') props.onClose();
    };
    setTimeout(() => document.addEventListener('mousedown', onDocClick), 0);
    document.addEventListener('keydown', onKey);
    onCleanup(() => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    });
  });

  const onSignOut = async () => {
    props.onClose();
    await signOutOfFirebase();
    navigate('/login');
  };

  return (
    <div
      ref={containerRef}
      class="absolute bottom-16 left-3 right-3 z-50 rounded-lg border border-neutral-200 bg-surface p-2 shadow-lg dark:border-neutral-700 dark:bg-surface-dark"
    >
      <div class="px-2 pt-1 pb-1 text-xs text-neutral-500">Theme</div>
      <For each={THEME_MODES}>
        {(m) => (
          <button
            type="button"
            class="flex w-full items-center justify-between rounded-lg px-2 py-2 text-left text-sm transition-colors hover:bg-neutral-100 motion-reduce:transition-none dark:hover:bg-neutral-800"
            onClick={() => setThemeMode(m.value)}
          >
            {m.label}
            <Show when={themeMode() === m.value}>
              <Icon name="check" class="text-blue-600" />
            </Show>
          </button>
        )}
      </For>
      <div class="my-1 border-t border-neutral-200 dark:border-neutral-700" />
      <div class="px-2 py-2 text-xs text-neutral-500">{session()?.email}</div>
      <button
        type="button"
        class="w-full rounded-lg px-2 py-2 text-left text-sm transition-colors hover:bg-neutral-100 dark:hover:bg-neutral-800"
        onClick={() => {
          props.onClose();
          navigate('/profile');
        }}
      >
        Profile
      </button>
      <button
        type="button"
        class="w-full rounded-lg px-2 py-2 text-left text-sm transition-colors hover:bg-neutral-100 dark:hover:bg-neutral-800"
        onClick={() => {
          props.onClose();
          navigate('/profile/settings');
        }}
      >
        Personal settings
      </button>
      <button
        type="button"
        class="w-full rounded-lg px-2 py-2 text-left text-sm transition-colors hover:bg-neutral-100 dark:hover:bg-neutral-800"
        onClick={onSignOut}
      >
        Sign out
      </button>
      <div class="mt-1 border-t border-neutral-200 px-2 pt-2 pb-1 dark:border-neutral-700">
        <div class="text-xs text-neutral-500">Web Version</div>
        <a
          href={`https://github.com/Zimpligital/uchat-team-management/commit/${__COMMIT_HASH_FULL__}`}
          target="_blank"
          rel="noreferrer"
          class="text-xs text-neutral-400 uppercase transition-colors hover:text-neutral-600 dark:text-neutral-500 dark:hover:text-neutral-300"
        >
          {`${__COMMIT_HASH__}.${__COMMIT_DATE__}`}
        </a>
        <div class="mt-1 text-xs text-neutral-500">Api Version</div>
        <Show
          when={apiVersion.data?.data}
          fallback={
            <div class="text-xs text-neutral-400 dark:text-neutral-500">
              {apiVersion.isError ? 'unavailable' : '…'}
            </div>
          }
        >
          {(v) => (
            <a
              href={`https://github.com/Zimpligital/uchat-team-management/commit/${v().commitHashFull}`}
              target="_blank"
              rel="noreferrer"
              class="text-xs text-neutral-400 uppercase transition-colors hover:text-neutral-600 dark:text-neutral-500 dark:hover:text-neutral-300"
            >
              {`${v().commitHash}.${v().commitDate}`}
            </a>
          )}
        </Show>
      </div>
    </div>
  );
};
